import { useExplicitFreeTurnBrandingVariation } from '#pplx/frontend/apps/ask/generated/assignment-hooks/useExplicitFreeTurnBrandingVariation.js';
import { getAsiAccessDecision } from '#pplx/frontend/apps/ask/queries/billing-queries/credits/getAsiAccessDecision.js';
import { getAsiAccessDecisionQueryKey } from '#pplx/frontend/apps/ask/queries/query-keys/billingKeys.js';
import { useQuery } from '#pplx/frontend/react-query/useQuery.js';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
const FREE_TURN_BUDGET_TYPE = 'free_turn';
const UNRESOLVED_ACCESS_DECISION_REFETCH_INTERVAL_MS = 5_000;
export function getUnresolvedAsiAccessDecisionRefetchInterval(accessDecision) {
    return accessDecision == null ? UNRESOLVED_ACCESS_DECISION_REFETCH_INTERVAL_MS : false;
}
export function isComputerBudgetAvailable(decision) {
    return decision?.can_use_computer === true && decision.is_out_of_budget === false;
}
// A local task runs on this device, so only an explicit denial blocks it, not a decision the cloud
// has not returned (offline, still loading).
export function allowsLocalComputerTask(decision) {
    return decision?.can_use_computer !== false;
}
export function isFreeTurnBudget(decision) {
    return decision?.budget_type === FREE_TURN_BUDGET_TYPE;
}
export function useIsFreeTurnBudget({ enabled = true } = {}) {
    const { data: decision } = useAsiAccessDecision({ enabled });
    return isFreeTurnBudget(decision);
}
export function hasQuotaAccess(decision) {
    return decision?.can_use_computer === true && decision.budget_type === 'quota';
}
function isFreeTurnPreviewDecision(decision) {
    return (isComputerBudgetAvailable(decision) &&
        decision.budget_type === FREE_TURN_BUDGET_TYPE &&
        decision.is_out_of_credits === true);
}
export function useIsFreeTurnBudgetExhausted({ enabled = true, } = {}) {
    const { data: decision } = useAsiAccessDecision({ enabled });
    return isFreeTurnBudgetExhausted(decision);
}
export function isFreeTurnBudgetExhausted(decision) {
    return decision?.budget_type === FREE_TURN_BUDGET_TYPE && decision.is_out_of_budget === true;
}
export function isFreeTurnBudgetAvailable(decision) {
    // A free-turn preview decision already requires is_out_of_budget === false,
    // so the budget cannot simultaneously be exhausted.
    return isFreeTurnPreviewDecision(decision);
}
export function useIsFreeTurnPreview({ enabled = true } = {}) {
    const { data: decision } = useAsiAccessDecision({ enabled });
    return isFreeTurnPreviewDecision(decision);
}
export function useExplicitFreeTurnBranding({ enabled = true, } = {}) {
    const isFreeTurnPreview = useIsFreeTurnPreview({ enabled });
    // Resolve eligibility before evaluating Eppo so users who cannot see the
    // branding stay out of the experiment population.
    const { variation } = useExplicitFreeTurnBrandingVariation({
        defaultValue: false,
        skip: !enabled || !isFreeTurnPreview,
    });
    return variation && isFreeTurnPreview;
}
// Lazy (not a module-level constant) so the query key is resolved at call
// time rather than import time, keeping module mocks in tests working.
function getAsiAccessDecisionQueryOptions() {
    return {
        queryKey: getAsiAccessDecisionQueryKey(),
        queryFn: () => getAsiAccessDecision({ reason: 'asi-access-decision' }),
        staleTime: 1000 * 60 * 5,
    };
}
export function useAsiAccessDecision({ enabled = true, refetchInterval, refetchOnMount, } = {}) {
    const { data, isLoading, isError, isFetchedAfterMount, isFetching } = useQuery({
        ...getAsiAccessDecisionQueryOptions(),
        placeholderData: keepPreviousData,
        enabled,
        refetchInterval,
        refetchOnMount,
    });
    return {
        data: data ?? null,
        isLoading,
        isError,
        isFetchedAfterMount,
        isFetching: isFetching ?? false,
    };
}
/**
 * Awaits the in-flight ASI access-decision query (fetching if needed) so
 * event handlers don't act on unsettled hook state. Resolves to null on
 * fetch failure.
 */
export function useResolveAsiAccessDecision() {
    const queryClient = useQueryClient();
    return useCallback(async () => {
        try {
            return (await queryClient.ensureQueryData(getAsiAccessDecisionQueryOptions())) ?? null;
        }
        catch {
            return null;
        }
    }, [queryClient]);
}
export function useRefreshAsiAccessDecisionAfterFreeTurnUse({ enabled = true, } = {}) {
    const queryClient = useQueryClient();
    const shouldRefreshAccessAfterComputerSubmit = useIsFreeTurnPreview({ enabled });
    return useCallback(() => {
        if (!shouldRefreshAccessAfterComputerSubmit)
            return;
        void queryClient.invalidateQueries({ queryKey: getAsiAccessDecisionQueryKey() });
    }, [queryClient, shouldRefreshAccessAfterComputerSubmit]);
}
//# sourceMappingURL=useAsiAccessDecision.js.map